export const SHORTLIST_STORAGE_KEY = "mgh_shortlisted_properties";

/**
 * Safely retrieves all shortlisted properties from localStorage.
 */
export const getShortlistedProperties = () => {
  try {
    const raw = localStorage.getItem(SHORTLIST_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.error("Error reading shortlisted properties from storage:", err);
    return [];
  }
};

/**
 * Checks whether a given property ID is in the shortlist.
 */
export const isPropertyShortlisted = (propOrId) => {
  if (!propOrId) return false;
  const list = getShortlistedProperties();
  if (typeof propOrId === "object" && propOrId !== null) {
    const idA = propOrId._id ? String(propOrId._id) : "";
    const idB = propOrId.propertyID ? String(propOrId.propertyID) : "";
    const idC = propOrId.id ? String(propOrId.id) : "";
    return list.some(
      (item) =>
        (idA && (String(item._id) === idA || String(item.propertyID) === idA)) ||
        (idB && (String(item.propertyID) === idB || String(item._id) === idB)) ||
        (idC && (String(item.id) === idC || String(item._id) === idC))
    );
  }
  const idStr = String(propOrId);
  return list.some(
    (item) =>
      String(item._id) === idStr ||
      String(item.propertyID) === idStr ||
      String(item.id) === idStr
  );
};

/**
 * Toggles a property in the shortlist.
 * Normalizes property fields so cards always render smoothly.
 * @returns {boolean} true if added, false if removed
 */
export const toggleShortlistProperty = (property) => {
  if (!property) return false;
  const list = getShortlistedProperties();
  const idA = property._id ? String(property._id) : "";
  const idB = property.propertyID ? String(property.propertyID) : "";
  const idC = property.id ? String(property.id) : "";
  const propId = property._id || property.propertyID || property.id;
  const idStr = String(propId);

  const existsIndex = list.findIndex(
    (item) =>
      (idA && (String(item._id) === idA || String(item.propertyID) === idA)) ||
      (idB && (String(item.propertyID) === idB || String(item._id) === idB)) ||
      (idC && (String(item.id) === idC || String(item._id) === idC)) ||
      (idStr && (String(item._id) === idStr || String(item.propertyID) === idStr || String(item.id) === idStr))
  );

  let updatedList = [];
  let isAdded = false;

  if (existsIndex > -1) {
    updatedList = list.filter((_, idx) => idx !== existsIndex);
    isAdded = false;
  } else {
    // Normalize property payload
    const normalized = {
      _id: property._id || property.propertyID || `prop_${Date.now()}`,
      propertyID: property.propertyID || property._id || "",
      apartment:
        property.apartment ||
        property.apartmentName ||
        property.propertyTitle ||
        property.title ||
        "Premium Property",
      apartmentName:
        property.apartmentName || property.apartment || property.propertyTitle || "",
      visiblePrice:
        property.visiblePrice ||
        (property.expectedPrice ? `${property.expectedPrice}` : "") ||
        property.price ||
        "Price on Request",
      expectedPrice: property.expectedPrice || property.visiblePrice || "",
      noofBedrooms: property.noofBedrooms || property.bhk || 3,
      noofBathrooms: property.noofBathrooms || 2,
      carpetArea: property.carpetArea || property.superArea || property.builtUpArea || 1450,
      locality: property.locality || property.location || "Hyderabad",
      city: property.city || "Hyderabad",
      state: property.state || "Telangana",
      superCategory: property.superCategory || "Residential",
      subCategory: property.subCategory || "Apartment",
      category: property.category || "Sell",
      furnishing: property.furnishing || "Semi-Furnished",
      propertyFacing: property.propertyFacing || "East",
      propertyOnFloor: property.propertyOnFloor || property.floorNo || "3",
      noofFloor: property.noofFloor || property.totalFloors || "12",
      postUserName: property.postUserName || property.ownerName || "Owner",
      postUserMobile: property.postUserMobile || property.mobile || "9160213545",
      postUserPosition: property.postUserPosition || "Owner",
      DeskImg:
        Array.isArray(property.DeskImg) && property.DeskImg.length > 0
          ? property.DeskImg
          : Array.isArray(property.photos) && property.photos.length > 0
          ? property.photos
          : Array.isArray(property.images) && property.images.length > 0
          ? property.images
          : property.thumbnail
          ? [property.thumbnail]
          : ["/assets/images/why_choose_villa.jpg"],
      shortlistedAt: new Date().toISOString(),
    };

    updatedList = [normalized, ...list];
    isAdded = true;
  }

  try {
    localStorage.setItem(SHORTLIST_STORAGE_KEY, JSON.stringify(updatedList));
  } catch (err) {
    console.error("Error saving shortlisted property to storage:", err);
  }

  // Dispatch custom window event for instant cross-component updates
  try {
    window.dispatchEvent(
      new CustomEvent("mgh_shortlist_updated", {
        detail: { propId, isAdded, updatedList, count: updatedList.length },
      })
    );
  } catch (e) {
    // Fallback if CustomEvent fails
  }

  return isAdded;
};

/**
 * Removes a property from shortlist by ID.
 */
export const removeShortlistProperty = (propId) => {
  if (!propId) return [];
  const list = getShortlistedProperties();
  const idStr = String(propId);
  const updatedList = list.filter(
    (item) =>
      String(item._id) !== idStr &&
      String(item.propertyID) !== idStr &&
      String(item.id) !== idStr
  );

  try {
    localStorage.setItem(SHORTLIST_STORAGE_KEY, JSON.stringify(updatedList));
  } catch (err) {
    console.error("Error updating shortlist after removal:", err);
  }

  try {
    window.dispatchEvent(
      new CustomEvent("mgh_shortlist_updated", {
        detail: { propId, isAdded: false, updatedList, count: updatedList.length },
      })
    );
  } catch (e) {}

  return updatedList;
};

/**
 * Clears all items from the shortlist.
 */
export const clearAllShortlist = () => {
  try {
    localStorage.removeItem(SHORTLIST_STORAGE_KEY);
  } catch (err) {}

  try {
    window.dispatchEvent(
      new CustomEvent("mgh_shortlist_updated", {
        detail: { updatedList: [], isAdded: false, count: 0 },
      })
    );
  } catch (e) {}
};

/**
 * Returns current total count of shortlisted properties.
 */
export const getShortlistCount = () => {
  return getShortlistedProperties().length;
};
